
const API_KEY_STORAGE_KEY = 'SMARTLEAD_API_KEY'

export class APIKeyManager {
  private static instance: APIKeyManager
  private apiKey: string | null = null
  private isInitialized: boolean = false

  static getInstance(): APIKeyManager {
    if (!APIKeyManager.instance) {
      APIKeyManager.instance = new APIKeyManager()
    }
    return APIKeyManager.instance
  }

  constructor() {
    this.loadApiKey()
  }

  private async loadApiKey(): Promise<void> {
    try {
      let foundKey: string | null = null

      // PRIORITY 1: Check for environment variable (global/production key)
      const envKey = import.meta.env.VITE_SMARTLEAD_API_KEY
      if (envKey && envKey.trim()) {
        foundKey = envKey.trim()
      } else if (typeof window !== 'undefined') {
        const localKey = localStorage.getItem(API_KEY_STORAGE_KEY)
        if (localKey && localKey.trim()) {
          foundKey = localKey.trim()
        }
      }

      this.apiKey = foundKey || null
      this.isInitialized = true
    } catch (error) {
      console.error('Failed to load API key:', error)
      this.apiKey = null
      this.isInitialized = true
    }
  }

  getApiKey(): string | null {
    // PRIORITY 1: Environment variable (always preferred)
    const envKey = import.meta.env.VITE_SMARTLEAD_API_KEY
    if (envKey && envKey.trim()) {
      return envKey.trim()
    }

    // PRIORITY 2: Cached in-memory key
    if (this.apiKey) {
      return this.apiKey
    }

    // PRIORITY 3: localStorage fallback (development only)
    if (typeof window !== 'undefined') {
      const currentKey = localStorage.getItem(API_KEY_STORAGE_KEY)
      if (currentKey && currentKey.trim()) {
        this.apiKey = currentKey.trim()
        return this.apiKey
      }
    }
    
    return null
  }

  async setApiKey(key: string): Promise<void> {
    if (!key || key.trim() === '') {
      console.error('Cannot set empty API key')
      return
    }

    const trimmedKey = key.trim()
    this.apiKey = trimmedKey
    
    try {
      // Save to localStorage for this browser
      if (typeof window !== 'undefined') {
        localStorage.setItem(API_KEY_STORAGE_KEY, trimmedKey)
        console.log('🔑 API Key saved to localStorage')
      }
    } catch (error) {
      console.error('Failed to save API key:', error)
    }
  }

  clearApiKey(): void {
    this.apiKey = null
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(API_KEY_STORAGE_KEY)
        localStorage.removeItem('SMARTLEAD_API_CONFIGURED')
        console.log('🔑 API Key and configuration cleared from localStorage')
      }
    } catch (error) {
      console.error('Failed to clear API key:', error)
    }
  }

  isValid(): boolean {
    const key = this.getApiKey()
    return !!(key && key.length > 10)
  }

  // Try alternative storage methods if localStorage fails
  private tryAlternativeStorage(key: string): void {
    try {
      // Try sessionStorage as backup
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(API_KEY_STORAGE_KEY, key)
        console.log('🔑 API Key saved to sessionStorage as backup')
      }
    } catch (error) {
      console.error('Failed to save API key to alternative storage:', error)
    }
  }

  // Check if API key is still valid and recover if needed
  checkAndRecoverApiKey(): boolean {
    if (typeof window === 'undefined') return false
    
    const currentKey = localStorage.getItem(API_KEY_STORAGE_KEY)
    if (!currentKey && this.apiKey) {
      console.log('🔑 API Key missing from localStorage, attempting recovery...')
      try {
        localStorage.setItem(API_KEY_STORAGE_KEY, this.apiKey)
        console.log('🔑 API Key recovered successfully')
        return true
      } catch (error) {
        console.error('🔑 Failed to recover API key:', error)
        return false
      }
    }
    return !!currentKey
  }

  // Force reload from storage (useful for debugging)
  forceReload(): void {
    this.isInitialized = false
    this.apiKey = null
    this.loadApiKey()
  }
}

// Export singleton instance
export const apiKeyManager = APIKeyManager.getInstance()
